/**
 * Marketing > Marketing Guides — PDF guides corporate has made available to this
 * location. Search, filter by category, view in a new tab, or download.
 *
 * Gated fail-closed on the `marketing guides` module (nav requiresModuleActive +
 * RequireModule requireActive notFound in App.tsx; the API checks it too), and
 * on Marketing Access via the Marketing section. Corporate decides which guides
 * a location sees; this page only shows what the API returns.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Download, Eye, FileText, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { FilterSelect, type FilterSelectOption } from '@/components/ui/FilterSelect';
import { useToast } from '@/hooks/useToast';
import {
  downloadMarketingGuide,
  getMarketingGuides,
  viewMarketingGuide,
  type MarketingGuide,
} from '@/services/marketingGuides.service';

const ALL = 'all';
const UNCATEGORISED = 'none';

function formatFileSize(bytes: number): string {
  if (!bytes || bytes < 0) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function errorMessage(err: unknown): string {
  const e = err as { userMessage?: string; response?: { data?: { error?: { message?: string } } } };
  return e?.userMessage ?? e?.response?.data?.error?.message ?? 'Please try again.';
}

export default function MarketingGuidesPage() {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(ALL);
  const [busy, setBusy] = useState<{ id: number; action: 'view' | 'download' } | null>(null);

  const { data: guides = [], isLoading, isError } = useQuery({
    queryKey: ['marketing', 'guides'],
    queryFn: getMarketingGuides,
  });

  const categoryOptions = useMemo<FilterSelectOption[]>(() => {
    const counts = new Map<string, { label: string; count: number }>();
    let uncategorised = 0;
    for (const g of guides) {
      if (g.categoryId && g.categoryName) {
        const key = String(g.categoryId);
        counts.set(key, { label: g.categoryName, count: (counts.get(key)?.count ?? 0) + 1 });
      } else {
        uncategorised += 1;
      }
    }
    const options: FilterSelectOption[] = [{ label: 'All categories', value: ALL, count: guides.length }];
    for (const [value, { label, count }] of [...counts.entries()].sort((a, b) => a[1].label.localeCompare(b[1].label))) {
      options.push({ label, value, count });
    }
    if (uncategorised > 0 && counts.size > 0) {
      options.push({ label: 'Uncategorised', value: UNCATEGORISED, count: uncategorised });
    }
    return options;
  }, [guides]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return guides.filter((g) => {
      if (category === UNCATEGORISED && g.categoryId) return false;
      if (category !== ALL && category !== UNCATEGORISED && String(g.categoryId) !== category) return false;
      if (q) {
        const haystack = `${g.title} ${g.description ?? ''} ${g.categoryName ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [guides, search, category]);

  const handleView = async (guide: MarketingGuide) => {
    setBusy({ id: guide.id, action: 'view' });
    try {
      await viewMarketingGuide(guide.id);
    } catch (err) {
      toast.error('Could not open guide', errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async (guide: MarketingGuide) => {
    setBusy({ id: guide.id, action: 'download' });
    try {
      const result = await downloadMarketingGuide(guide.id);
      if (result === 'opened') {
        toast.info('Opened in a new tab', 'Use your browser\'s download button to save the PDF.');
      }
    } catch (err) {
      toast.error('Could not download guide', errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const showFilter = categoryOptions.length > 2;

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Marketing Guides" subtitle="Guides and resources provided by corporate." />

      {guides.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[200px] flex-1 md:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search guides..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              aria-label="Search guides"
            />
          </div>
          {showFilter && (
            <FilterSelect options={categoryOptions} value={category} onChange={setCategory} label="Filter by category" />
          )}
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-80 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          icon={<BookOpen className="h-8 w-8 text-slate-400" aria-hidden="true" />}
          title="Could not load guides"
          description="Refresh the page to try again."
        />
      ) : guides.length === 0 ? (
        <EmptyState
          icon={<BookOpen className="h-8 w-8 text-slate-400" aria-hidden="true" />}
          title="No marketing guides yet"
          description="Guides your corporate team shares with this location will appear here."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Search className="h-8 w-8 text-slate-400" aria-hidden="true" />}
          title="No guides match your search"
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
          {filtered.map((g) => (
            <div
              key={g.id}
              className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
            >
              <button
                type="button"
                onClick={() => handleView(g)}
                className="flex aspect-[4/3] w-full items-center justify-center overflow-hidden border-b border-slate-100 bg-slate-50"
                title={`Open ${g.title}`}
              >
                {g.thumbnailUrl ? (
                  <img src={g.thumbnailUrl} alt="" className="h-full w-full object-cover object-top" />
                ) : (
                  <FileText className="h-12 w-12 text-slate-300" aria-hidden="true" />
                )}
              </button>
              <div className="flex flex-1 flex-col gap-2 p-4">
                {g.categoryName && (
                  <div>
                    <Badge>{g.categoryName}</Badge>
                  </div>
                )}
                <h3 className="text-sm font-semibold text-slate-900">{g.title}</h3>
                {g.description && <p className="line-clamp-3 text-xs text-slate-600">{g.description}</p>}
                <p className="mt-auto text-xs text-slate-400">PDF · {formatFileSize(g.fileSize)}</p>
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                    leftIcon={<Eye className="h-4 w-4" />}
                    loading={busy?.id === g.id && busy.action === 'view'}
                    disabled={busy?.id === g.id}
                    onClick={() => handleView(g)}
                  >
                    View
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-1"
                    leftIcon={<Download className="h-4 w-4" />}
                    loading={busy?.id === g.id && busy.action === 'download'}
                    disabled={busy?.id === g.id}
                    onClick={() => handleDownload(g)}
                  >
                    Download
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
